import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  InputGroupAddon,
  inputVariants,
} from '@arcadiapower/design-system';
import { Search } from 'lucide-react';
import { debounce } from 'lodash-es';
import { Provider } from '@arc-connect/schema';
import { SearchWrapper } from './provider-combobox.style';

export type Props = {
  providers: Provider[];
  selectedProvider?: Provider;
  onSelect: (provider: Provider | undefined) => void;
  onSearch: (searchTerm: string) => void;
  initialSearchInput?: string;
  label: string;
  description: string;
  name: string;
  errorText?: string;
  autoFocus?: boolean;
};

// Base UI reports every input change, including its own housekeeping (resetting the text
// when the popup closes, syncing it to a cleared selection). Only these reasons are the
// user editing the query; 'item-press' is Base UI filling in the chosen provider's name.
const USER_EDIT_REASONS = new Set(['input-change', 'input-paste']);
const ITEM_PRESS_REASON = 'item-press';

const isSameProvider = (a: Provider, b: Provider) => a.id === b.id;
const providerLabel = (provider: Provider) => provider.name;
const providerValue = (provider: Provider) => provider.id;

// Mirrors the DS `InputGroupInput` overrides so the control sits flush inside the group.
const INPUT_GROUP_CONTROL_CLASSES =
  'h-full flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0';

// The group paints the border, ring, shadow and background; the control must not. The DS
// `InputGroupInput` gets this for free because its `cn()` (tailwind-merge) drops the base
// utilities that the overrides above conflict with. Plain string concatenation keeps both,
// and stylesheet order then decides — which is how the invalid state grew a second red ring
// inside the group's. Strip those base utilities before applying our overrides.
const isGroupOwnedUtility = (cls: string) =>
  /^(aria-invalid:|focus-visible:)?(border|ring|shadow|bg)(-|$)/.test(cls);
const controlBaseClasses = inputVariants({ size: 'default' })
  .split(/\s+/)
  .filter(cls => !isGroupOwnedUtility(cls))
  .join(' ');

// DS `ComboboxInput` renders its control through `InputGroupInput`, which is not wrapped in
// `forwardRef`. Under React 18 that drops the ref Base UI attaches, so it never learns where
// the input is: focus jumps into the popup and selecting an item doesn't fill the input.
// Rendering the control ourselves restores the ref until the DS forwards it.
const ComboboxControl = forwardRef<HTMLInputElement, ComponentPropsWithoutRef<'input'>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      data-slot="input-group-control"
      className={[controlBaseClasses, INPUT_GROUP_CONTROL_CLASSES, className]
        .filter(Boolean)
        .join(' ')}
      {...props}
    />
  )
);
ComboboxControl.displayName = 'ComboboxControl';

export const ProviderCombobox = ({
  providers,
  selectedProvider,
  onSelect,
  onSearch,
  initialSearchInput,
  label,
  description,
  name,
  errorText,
  autoFocus,
}: Props): JSX.Element => {
  const id = useId();
  // Anchor the popup to the whole field, not just the <input> after the search icon.
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => initialSearchInput ?? '');

  const debouncedSearch = useMemo(() => debounce(onSearch, 300), [onSearch]);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleInputValueChange = useCallback(
    (value: string, details: { reason: string }) => {
      if (USER_EDIT_REASONS.has(details.reason)) {
        setInputValue(value);
        debouncedSearch(value);
      } else if (details.reason === ITEM_PRESS_REASON) {
        setInputValue(value);
      }
    },
    [debouncedSearch]
  );

  return (
    <SearchWrapper>
      <Field data-invalid={errorText ? true : undefined}>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <Combobox<Provider>
          name={name}
          items={providers}
          // Results are already filtered server-side; let Base UI show them as-is.
          filter={null}
          value={selectedProvider ?? null}
          onValueChange={provider => onSelect(provider ?? undefined)}
          isItemEqualToValue={isSameProvider}
          itemToStringLabel={providerLabel}
          itemToStringValue={providerValue}
          inputValue={inputValue}
          onInputValueChange={handleInputValueChange}
          // Never show an empty popup: the spinner and "no results" copy render below the field.
          open={isOpen ? providers.length > 0 : false}
          onOpenChange={setIsOpen}
        >
          <div ref={anchorRef}>
            <ComboboxInput
              id={id}
              render={<ComboboxControl />}
              showTrigger={false}
              autoFocus={autoFocus}
              autoComplete="off"
              aria-invalid={errorText ? true : undefined}
            >
              <InputGroupAddon align="inline-start">
                <Search aria-hidden="true" strokeWidth={1.5} />
              </InputGroupAddon>
            </ComboboxInput>
          </div>
          <ComboboxContent anchor={anchorRef}>
            <ComboboxList>
              {(provider: Provider) => (
                <ComboboxItem key={provider.id} value={provider}>
                  {provider.name}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        {errorText ? (
          <FieldError>{errorText}</FieldError>
        ) : (
          <FieldDescription>{description}</FieldDescription>
        )}
      </Field>
    </SearchWrapper>
  );
};
